<template>
  <div
    v-if="loaded"
    class="flex w-full h-full md:overflow-x-hidden"
    :class="[isDarkMode ? 'bg-primary' : 'bg-book', { 'flex-col': showUpgradeToProBar }]"
  >
    <UpgradeToProBar
      v-if="showUpgradeToProBar"
      @hide="showUpgradeBar = false"
    />

    <MainSidebar />

    <section class="anim-position-width relative flex flex-col w-full" :class="[computedClasses, computedHeight]">
      <LoadingModule
        :loading="bookLoading && !mainLoading && (isBookPage || isToolPage || isDiscussionPage)"
        class="z-50"
        :class="isMobile ? 'fixed' : 'absolute'"
      />

      <LoadingModule class="fixed" :loading="mainLoading" />

      <BooksInstruction v-if="hasBooks" />

      <UpgradeToProOnboardingModal />

      <HeaderLayout
        v-if="!isBooksPage"
        @toggleSidebar="toggleSidebar"
      />

      <main
        id="main"
        class="overflow-y-auto"
        :class="[
          isBooksPage || (isMobile && isBookPage) ? 'h-full' : 'h-calc-header',
          !isBookInstructionAccepted && isBooksPage ? 'bg-onboarding-backdrop' : '',
          {'mt-60 md:mt-0 ': isToolPage}
        ]"
      >
        <router-view />
      </main>
    </section>

    <SettingsModal />
    <UpgradeProBadge v-if="isFreePlan" />
  </div>
</template>

<script>
import HeaderLayout from '@/components/layout/HeaderLayout'
import { useStore } from 'vuex'
import { preventBodyScroll, updatePreviousThemesLocalStorage, dispatchChangingDesign } from '@/core/helper-functions'
import localStorageService from '@/core/local-storage'
import { settingsService } from '@/services'
import BooksInstruction from '@/components/instructions/BooksInstruction'
import UpgradeToProOnboardingModal from '@/components/instructions/UpgradeToProOnboardingModal'
import UpgradeToProBar from '@/components/instructions/UpgradeToProBar'
import UpgradeProBadge from '@/components/shared/UpgradeProBadge'
import LoadingModule from '@/components/shared/LoadingModule'
import SettingsModal from '@/components/settings/SettingsModal'
import MainSidebar from '@/components/sidebar/MainSidebar'

import { defineComponent, computed, ref, onMounted, onBeforeUnmount, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'

import useSocket from '@/socketService/Socket.js'

export default defineComponent({
  components: {
    HeaderLayout,
    BooksInstruction,
    UpgradeToProOnboardingModal,
    UpgradeToProBar,
    UpgradeProBadge,
    LoadingModule,
    SettingsModal,
    MainSidebar
  },

  setup () {
    const { state, getters, dispatch, commit } = useStore()
    const route = useRoute()
    const router = useRouter()

    const { initSocket } = useSocket()

    initSocket()

    const loaded = ref(false)
    const showUpgradeBar = ref(false)
    // state
    const token = computed(() => state.auth.token)
    const settings = computed(() => state.settings.settings)
    const previousTheme = computed(() => state.settings.previousTheme)
    const previousDarkTheme = computed(() => state.settings.previousDarkTheme)
    const isFocusMode = computed(() => state.settings.isFocusMode)
    const showSidebar = computed(() => state.settings.showSidebar)
    const bookLoading = computed(() => state.general.bookLoading)
    const mainLoading = computed(() => state.general.loading)
    const user = computed(() => state.profile.user)
    const projectInstructionsActiveStep = computed(() => state.profile.activeStep)
    const isAutoSwitchingThemeEnable = computed(() => state.settings.isAutoSwitchingThemeEnable)
    const team = computed(() => state.team.team)
    // getters
    const isMobile = computed(() => getters.isMobile)
    const isDarkMode = computed(() => getters.isDarkMode)
    const hasBooks = computed(() => getters.hasBooks)
    const isTeamAccount = computed(() => getters.isTeamAccount)
    const isBookInstructionAccepted = computed(() => getters.isBookInstructionAccepted)
    const isProjectInstructionAccepted = computed(() => getters.isProjectInstructionAccepted)
    const isFreePlan = computed(() => getters.isFreePlan)
    const isPaperShape = computed(() => getters.isPaperShape)
    const showUpgradeToProBar = computed(() => {
      return user.value?.profile.showUpgradeToPro <= 5 &&
        isBookInstructionAccepted.value &&
        isProjectInstructionAccepted.value &&
        !isTeamAccount.value &&
        showUpgradeBar.value
    })

    const isBooksPage = computed(() => ['Books', 'TeamBooks'].includes(route.name))

    const isBookPage = computed(() => ['Book', 'TeamBook', 'Headquarters'].includes(route.name))

    const isToolPage = computed(() => ['WeekPlanner', 'WeekPlannerTeam', 'TaskBoard', 'TaskBoardTeam', 'Discussions', 'Chat', 'FileManager', 'FileManagerTeam'].includes(route.name))

    const isTaskBoard = computed(() => ['TaskBoard', 'TaskBoardTeam'].includes(route.name))

    const isDiscussionPage = computed(() => route.name === 'Discussion')

    const projectInstructionsActiveThirdStep = computed(() => !isProjectInstructionAccepted.value && projectInstructionsActiveStep.value === 3)

    const computedClasses = computed(() => {
      let bg = isProjectInstructionAccepted.value ? isPaperShape.value ? 'bg-book' : 'xxl:p-0' : ''
      if (isBooksPage.value) { bg = '' }
      if (isToolPage.value) { bg = 'bg-primary' }

      let classes = bg

      if (showSidebar.value) {
        classes += ' w-full md:w-calc-sidebar left-sidebar'
      } else {
        classes += ' w-full left-0'
      }

      return classes
    })

    const computedHeight = computed(() => {
      if (showUpgradeToProBar.value) {
        return isMobile.value ? ' h-calc-150 mt-150' : ' h-calc-50'
      }
      return ''
    })

    const mainClasses = computed(() => {
      let classes = ''

      if (isProjectInstructionAccepted.value) {
        classes = isToolPage.value || isBookPage.value || isBooksPage.value
          ? isDarkMode.value
            ? 'bg-canvas'
            : isBookPage.value && 'bg-primary' : ''
      }

      return classes
    })

    watch(isMobile, (value) => commit('updateSidebar', !value))

    watch(showSidebar, (value) => {
      if (isMobile.value) preventBodyScroll(value)
    })

    watch(() => route.name, (value) => {
      if (!isMobile.value && (value === 'Books' || value === 'TeamBooks')) commit('updateSidebar', true)
    })

    watch(projectInstructionsActiveThirdStep, (value) => {
      if (value) preventBodyScroll(true)
    })

    onMounted(async () => {
      const planId = localStorageService.getItem('planId')

      if (planId) updatePlan(planId)

      commit('updateSidebar', !isMobile.value)

      const team = await dispatch('getTeam')
      const user = await dispatch('getProfile')

      if (['Books', 'Book', 'TaskBoard', 'WeekPlanner', 'FileManager'].includes(route.name)) {
        if (!user.profile.hasPersonal) {
          router.push({ name: 'TeamBooks', params: { team: team.link } })
        } else {
          commit('setTeamId', null)
        }
      } else if (['TeamBooks', 'TeamBook', 'TaskBoardTeam', 'WeekPlannerTeam', 'Discussions', 'FileManagerTeam'].includes(route.name)) {
        if (!team) {
          router.push({ name: 'Books' })
        } else if (route.params.team !== team.link) {
          router.push({ name: 'TeamBooks', params: { team: team.link } })
        }
      }

      await dispatch('getGlobalData')

      loaded.value = true
      autoSwitchingTheme()

      if (token.value && loaded.value) {
        const page = localStorageService.getItem('lastOpenedProject')
        if (page) {
          if (isTeamAccount.value) {
            router.push({
              name: 'TeamBook',
              params: { team: team.link, id: page.bookId },
              query: { page: page.projectId }
            })
          } else {
            router.push(
              {
                name: 'Book',
                params: { id: page.bookId },
                query: { page: page.projectId }
              })
              .catch(() => {})
          }
        }
      }

      document.addEventListener('keydown', hotKeys)
      document.addEventListener('mousedown', mouseDown)
    })

    onBeforeUnmount(() => {
      document.removeEventListener('keydown', hotKeys)
      document.removeEventListener('mousedown', mouseDown)
      window.matchMedia('(prefers-color-scheme: dark)').removeEventListener('change', toggleTheme)
    })

    async function updatePlan (id) {
      localStorageService.removeItem('planId')
      const currentPlan = await dispatch('getCurrentPlan')

      if (currentPlan.planId === +route.query.planId || currentPlan.planId === 9 || currentPlan.planId === 10) {
        router.push({ name: 'Books' })
      } else {
        dispatch('upgradePlan', {
          planId: id,
          userId: user.value?.id
        }).then(res => {
          commit('loading', true)

          const stripe = window.Stripe(process.env.VUE_APP_STRIPE)
          stripe.redirectToCheckout({ sessionId: res })
            .then(() => {})
            .catch(() => commit('loading', false))
        })
      }
    }

    function hotKeys (event) {
      if (event.ctrlKey || event.metaKey) {
        // ctrl + 0 / cmd + 0 - open shortcuts
        if (event.keyCode === 48) {
          commit('changeActiveState', 'Keyboard')
          commit('toggleSettingsModal', true)
          commit('toggleSearch', false)
        }
        // ctrl + ? / cmd + ? - change theme
        if (event.keyCode === 191) {
          updateDesign()
        }
        // ctrl + k - search modal
        if (event.keyCode === 75) {
          event.preventDefault()
          commit('toggleSearch', true)
          commit('toggleSettingsModal', false)
        }
      }
    }

    function autoSwitchingTheme () {
      const systemDarkTheme = window.matchMedia('(prefers-color-scheme: dark)')

      systemDarkTheme.addEventListener('change', toggleTheme)
    }

    function toggleTheme (e) {
      if (isAutoSwitchingThemeEnable.value) {
        if ((e.matches && !isDarkMode.value) || (!e.matches && isDarkMode.value)) {
          updateDesign()
        }
      }
    }

    function mouseDown () {
      if (window.getSelection) {
        window.getSelection().removeAllRanges()
      }
    }

    function updateDesign () {
      let theme = ''
      const previousThemes = localStorageService.getItem('previous-themes') || {}
      const userPreviousThemes = previousThemes[user.value?.id] || {}

      if ((!settings.value.theme.includes('Dark')) && (settings.value.theme !== 'Dracula')) {
        theme = previousDarkTheme.value || userPreviousThemes?.dark || 'Dark Orange'
        commit('updatePreviousTheme', settings.value.theme)
        updatePreviousThemesLocalStorage(user.value, 'light', settings.value.theme)
      } else {
        theme = previousTheme.value || userPreviousThemes?.light || 'Magenta'
        commit('updatePreviousDarkTheme', settings.value.theme)
        updatePreviousThemesLocalStorage(user.value, 'dark', settings.value.theme)
      }

      const newSettings = { ...settings.value, theme: theme }

      commit('updateSettings', newSettings)
      settingsService.update(newSettings)

      dispatchChangingDesign(newSettings)
    }

    function toggleSidebar () {
      commit('updateSidebar', !showSidebar.value)
    }

    return {
      // data
      loaded,
      showUpgradeBar,
      // computed
      token,
      settings,
      previousTheme,
      previousDarkTheme,
      isFocusMode,
      showSidebar,
      bookLoading,
      mainLoading,
      isBooksPage,
      isBookPage,
      isToolPage,
      isTaskBoard,
      isDiscussionPage,
      showUpgradeToProBar,
      isPaperShape,
      projectInstructionsActiveThirdStep,
      user,
      projectInstructionsActiveStep,
      isAutoSwitchingThemeEnable,
      team,
      isMobile,
      isDarkMode,
      hasBooks,
      isTeamAccount,
      isBookInstructionAccepted,
      isProjectInstructionAccepted,
      isFreePlan,
      computedClasses,
      computedHeight,
      mainClasses,
      // functions
      toggleSidebar

    }
  }
})
</script>

<style lang="scss" scoped>
@media (min-width: 1320px) {
  .main-wrapper {
    width: calc(50% + 440px);
    left: 0;
    margin-left: auto;
  }
}
@media (min-width: 1266px) {
  .margin-left-40 {
    margin-left: -40px;
  }
}
</style>
